import { Download } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, ReactNode } from 'react';
import CreateForm from '../../../../components/ui-component/form-component/create-form/CreateForm';
import MUIAlert from '../../../../components/ui-component/mui-alert/MUIAlert';
import { useApplication } from '../../../../context/ApplicationContext';
import { ANTRAGS_FORMULAREN, FORM_FIELDS_TYPES } from '../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../lib/types/types';
import { isTrue } from '../../../../utils/helper';
import { DownloadAbschlussBerichtPDF, DownloadMittelabrufPDF, DownloadVerwendungsnacheisPDF, DownloadZwischenBerichtPDF } from '../DownloadAntragPDF';

// import schemas
import Schema from '.././../../../lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const AbschlussSeiteBox: FC<{ children: ReactNode }> = ({ children }) => {
	return <Box sx={{ width: '100%', mt: '1rem' }}>{children}</Box>;
};
export default AbschlussSeiteBox;

export const AbschlussSeiteHochladeFeldBox: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<Box sx={{ display: 'flex', width: '100%', alignItems: 'baseline' }}>
			<Box sx={{ width: '50%', paddingLeft: '1rem' }}>{children}</Box>
		</Box>
	);
};

export const AbschlussSeiteHinweisFeldBox: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<Box sx={{ mt: '1rem' }}>
			<Divider />
			{children}
		</Box>
	);
};

export const GefoerderteSummeProgrammInfo: FC<{ application: any; hideFoerderQuoteTabelle?: boolean }> = ({ application, hideFoerderQuoteTabelle = true }) => {
	if (get(application, P.Allgemein_Antragsformular.path) === ANTRAGS_FORMULAREN.StartUpStipendium || hideFoerderQuoteTabelle) return <></>;
	const formStrucFoerderQuoteTabelle = () => {
		// Hilfsfunktion, die überprüft, ob in der Tabelle `Foerderquote_Antragsberechtigte`
		// mindestens ein Eintrag einen Wert im Feld `Foerdergegenstand` hat.
		// Wenn ja, wird false zurückgegeben, andernfalls true.
		// Diese Funktion wird verwendet, um zu entscheiden, ob die Spalte `Foerdergegenstand` in der Tabelle angezeigt werden soll.
		function hasNoFoerdergegenstand() {
			const foerderQuoteTabelle = get(application, P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte.path, []);
			// Wenn mindestens ein Element den Pfad `Foerdergegenstand` hat und dieser einen Wert hat,
			// soll false zurückgegeben werden.
			for (const item of foerderQuoteTabelle) {
				if (item.Foerdergegenstand !== undefined && item.Foerdergegenstand !== null) {
					return false;
				}
			}

			// Wenn kein Element den Pfad `Foerdergegenstand` hat oder alle Werte leer sind,
			// gib true zurück.
			// das heißt, die Spalte `Foerdergegenstand` wird ausgeblendet
			return true;
		}

		let struc: FormStrucItem[] = [
			{
				fields: [
					{
						...P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte,
						type: FORM_FIELDS_TYPES.componentTable,
						hideDeleteButton: true,
						tableLabel: 'Art und Umfang der max. Förderung',
						hideColumns: isTrue(hasNoFoerdergegenstand()) ? ['Foerdergegenstand'] : [],
						fieldProps: {
							Antragsberechtigte: { pathForLabel: 'attributes.Allgemein_Bezeichnung' },
							Foerderquote: { format: 'percentage' },
							Max_Foerdersumme: { format: 'currency' },
						},
					},
				],
				userProps: { columnToDivide: 1 },
			},
		];
		return struc;
	};

	return (
		<>
			{' '}
			<Box sx={{ mt: '1rem' }}>
				<MUIAlert
					infoType={'warning'}
					title='Hinweis:'
					message={
						<>
							Bitte überprüfen Sie die von Ihnen beantragte Förderung und die daraus berechnete Förderquote. Die maximal zulässige Förderquote
							/ maximale Fördersumme (siehe Tabelle unten) dürfen nicht überschritten werden.
						</>
					}
				/>

				<CreateForm formStruc={formStrucFoerderQuoteTabelle()} formValues={application} updateFormValuesFunc={() => {}} readOnly={true} />
			</Box>
		</>
	);
};

export const FinaleSchrittInfoBoxMittelabruf: FC<{}> = () => {
	const { application, readOnly, saveApplication } = useApplication();

	async function handleDownloadZwischenBericht() {
		DownloadMittelabrufPDF(application, readOnly, saveApplication);
	}

	return (
		<>
			<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
				<Typography variant='h6'>Finale Schritte</Typography>
				<Typography>Damit Sie den Mittelabruf einreichen können, möchten wir Sie bitten folgende Schritte durchzuführen:</Typography>
				<Typography>1. Laden Sie sich zunächst Ihren ausgefüllten Mittelabruf herunter</Typography>
				<Button variant='outlined' startIcon={<Download />} onClick={() => handleDownloadZwischenBericht()}>
					Mittelabruf herunterladen
				</Button>
				<Typography>
					2. Bitte unterschreiben Sie den Bericht und senden Sie an:
					<br />
					HA Hessen Agentur GmbH
					<br />
					Mainzer Str. 118
					<br />
					65189 Wiesbaden
				</Typography>
				<Typography>3. Laden Sie das unterschriebene Dokument über das Uploadfeld hoch</Typography>
			</Stack>
		</>
	);
};

export const FinaleSchrittInfoBoxVerwendungsnachweis: FC<{}> = () => {
	const { application, readOnly, saveApplication } = useApplication();

	async function handleDownloadZwischenBericht() {
		DownloadVerwendungsnacheisPDF(application, readOnly, saveApplication);
	}

	return (
		<>
			<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
				<Typography variant='h6'>Finale Schritte</Typography>
				<Typography>Damit Sie den Verwendungsnachweis einreichen können, möchten wir Sie bitten folgende Schritte durchzuführen:</Typography>
				<Typography>1. Laden Sie sich zunächst Ihren ausgefüllten Verwendungsnachweis herunter</Typography>
				<Button variant='outlined' startIcon={<Download />} onClick={() => handleDownloadZwischenBericht()} sx={{ maxWidth: 'fit-content' }}>
					Verwendungsnachweis herunterladen
				</Button>
				<Typography>
					2. Bitte unterschreiben Sie den Bericht und senden Sie an:
					<br />
					HA Hessen Agentur GmbH
					<br />
					Mainzer Str. 118
					<br />
					65189 Wiesbaden
				</Typography>
				<Typography>3. Laden Sie das unterschriebene Dokument über das Uploadfeld hoch</Typography>
			</Stack>
		</>
	);
};

export const FinaleSchritteInfoBosZwischenBericht: FC<{}> = () => {
	const { application, readOnly, saveApplication } = useApplication();

	async function handleDownloadZwischenBericht() {
		await DownloadZwischenBerichtPDF(application, readOnly, saveApplication);
	}

	return (
		<>
			<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
				<Typography variant='h6'>Finale Schritte</Typography>
				<Typography>Damit Sie den Zwischenbericht einreichen können, möchten wir Sie bitten folgende Schritte durchzuführen:</Typography>
				<Typography>1. Laden Sie sich zunächst Ihren ausgefüllten Zwischenbericht herunter</Typography>
				<Button variant='outlined' startIcon={<Download />} onClick={() => handleDownloadZwischenBericht()}>
					Bericht herunterladen
				</Button>
				<Typography>
					2. Bitte unterschreiben Sie den Bericht und senden Sie an:
					<br />
					HA Hessen Agentur GmbH
					<br />
					Mainzer Str. 118
					<br />
					65189 Wiesbaden
				</Typography>
				<Typography>3. Laden Sie das unterschriebene Dokument über das Uploadfeld hoch</Typography>
			</Stack>
		</>
	);
};

export const FinaleSchritteInfoBoxAbschlussbericht: FC<{}> = () => {
	const { application, readOnly, saveApplication } = useApplication();

	const handleDownloadAntrag = () => {
		DownloadAbschlussBerichtPDF(application, readOnly, saveApplication);
	};

	return (
		<>
			<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
				<Typography variant='h6'>Finale Schritte</Typography>
				<Typography>Damit Sie den Abschlussbericht einreichen können, möchten wir Sie bitten folgende Schritte durchzuführen:</Typography>
				<Typography>1. Laden Sie sich zunächst Ihren ausgefüllten Abschlussbericht herunter</Typography>
				<Button variant='outlined' startIcon={<Download />} onClick={() => handleDownloadAntrag()}>
					Bericht herunterladen
				</Button>
				<Typography>
					2. Bitte unterschreiben Sie den Bericht und senden Sie an:
					<br />
					HA Hessen Agentur GmbH
					<br />
					Mainzer Str. 118
					<br />
					65189 Wiesbaden
				</Typography>
				<Typography>3. Laden Sie das unterschriebene Dokument über das Uploadfeld hoch</Typography>
			</Stack>
		</>
	);
};
